import AbstractInstantiationError from '../../util'
import {easingFormulas} from '..'
import cloneDeep from 'lodash.clonedeep'

/**
 * The base tween class.
 *
 * @param  {PIXI.Sprite}  sprite            the sprite to tween
 * @param  {number}  data                   contains the tween values
 * @param  {number}  duration               the number of frames that the tween will take to complete
 * @param  {String}  [type='smoothstep']    the type of easing
 * @param  {Boolean} [yoyo=false]           should the sprite yoyo
 * @param  {Number}  [delayBeforeRepeat=0]  Delay in frames before repeating
 * @abstract
 */
export default class BaseTween {
  constructor (
    sprite,
    data,
    duration,
    type = 'smoothstep',
    yoyo = false,
    delayBeforeRepeat = 0
  ) {
    // If the tween is a bounce type (a spline), set the
    // start and end magnitude values
    this.typeArray = type.split(' ')
    if (this.typeArray[0] === 'bounce') {
      this.startMagnitude = parseInt(this.typeArray[1])
      this.endMagnitude = parseInt(this.typeArray[2])
    }

    // Clone the data to make sure we don't modify any external objects unintentionally
    this.data = cloneDeep(data)

    this.duration = duration
    this.delayBeforeRepeat = delayBeforeRepeat
    this.yoyo = yoyo
    this.type = type
    this.sprite = sprite
    this.frameCounter = 0
  }

  restart () {
    this.frameCounter = 0

    this.play()
  }

  /**
   * Advances to the next frame.
   *
   * @param  {number}   deltaTime the deltaTime passed in from the ticker
   */
  next (deltaTime) {
    let curvedTime
    // Find the normalized value
    const normalizedTime = this.frameCounter / this.duration

    // Select the correct easing function from the
    // `ease` object’s library of easing functions

    // If it's not a spline, use one of the ordinary easing functions
    if (this.typeArray[0] !== 'bounce') {
      curvedTime = easingFormulas[this.type](normalizedTime)
    }

    // If it's a spline, use the `spline` function and apply the
    // 2 additional `type` array values as the spline's start and
    // end points
    else {
      curvedTime = this.easingFormulas.spline(normalizedTime, this.startMagnitude, 0, 1, this.endMagnitude)
    }

    this._modifyProperty(curvedTime)

    this.frameCounter++
  }

  /**
   * The `update` method will be called on each frame by the game loop.
   *
   * @param  {number} deltaTime the time passed in by the ticker
   */
  update (deltaTime) {
    if (this._playing) {
      // If the elapsed frames are less than the total frames,
      // use the tweening formulas to move the sprite
      if (this.frameCounter < this.duration) {
        this.next()
      } else { // When the tween has finished playing, run the end tasks
        this._setUpForEnd()
        this.end()
      }
    }
  }

  /**
   * The `end` method will be called when the tween is finished.
   */
  end () {
    this.pause()

    // Call the tween's `onComplete` method, if it's been assigned
    if (this.onComplete) this.onComplete()

    // If the tween's `yoyo` property is `true`, create a new tween
    // using the same values, but use the current tween's `startValue`
    // as the next tween's `endValue`
    if (this.yoyo) {
      this.wait(this.delayBeforeRepeat).then(() => {
        this._setUpForYoyo()
        this.restart()
      })
    }
  }

  /**
   * Play the tween.
   */
  play () {
    this._playing = true
  }

  /**
   * Pause the tween.
   */
  pause () {
    this._playing = false
  }

  get playing () {
    return this._playing
  }

  _setUpForYoyo () {
    throw AbstractInstantiationError(this)
  }

  _setUpForEnd () {
    throw AbstractInstantiationError(this)
  }

  _modifyProperty (curvedTime) {
    throw AbstractInstantiationError(this)
  }
}



// WEBPACK FOOTER //
// ./src/base-tween.js